import { Fragment, useState } from 'react';
import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonInput,
    IonItem,
    IonLabel,
    IonPage,
    IonSpinner,
    IonText,
    IonTitle,
    IonToolbar
} from '@ionic/react';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db, getCompanyId } from '../firebase/firebase';
import { getUsers } from '../firebase/users';
import { getClients } from '../firebase/clients';
import './ReportsPage.css';

const STATUS_DEFINITIONS = [
    { id: 1, label: 'umówiony' },
    { id: 2, label: 'odwołany (klient)' },
    { id: 3, label: 'odwołany (terapeuta)' },
    { id: 4, label: 'nieobecny' },
    { id: 5, label: 'zrealizowany' }
];

const STATUS_LOOKUP = STATUS_DEFINITIONS.reduce((acc, status) => {
    acc[status.id] = status.label;
    return acc;
}, {} as Record<number, string>);

const REALIZED_STATUS_ID = 5;

type SessionRow = {
    eventId: string;
    date: string;
    start: string;
    end: string;
    startMs: number;
    endMs: number;
    durationMinutes: number;
    price: number;
    serviceCatalogRate: number;
    serviceTherapistRate: number;
    serviceHourlyRate: number;
    durationLabel: string;
    statusId: number;
    statusLabel: string;
    serviceName: string;
    clientName: string;
    clientPhone: string;
    clientEmail: string;
    comments?: string;
    managerComments?: string;
    hasService: boolean;
    isGroupClasses: boolean;
};

type TherapistReport = {
    therapistId: string;
    therapistName: string;
    baseRate: number;
    percentageRate: number;
    individualRate: number;
    overlapCount: number;
    overlapDetails: { startMs: number; endMs: number }[];
    sessions: SessionRow[];
    totalMinutes: number;
    statusSummary: Record<number, number>;
    serviceSummary: Record<string, number>;
};

type RevenueRow = {
    serviceName: string;
    hourlyRate: number;
    totalMinutes: number;
    totalHours: number;
    revenue: number;
    occurrences: { eventId: string; date: string; start: string; end: string; startMs: number }[];
    omittedOccurrences: { eventId: string; date: string; start: string; end: string; startMs: number }[];
};

type TherapistRevenueReport = {
    therapistId: string;
    therapistName: string;
    overlapCount: number;
    serviceHours: number;
    serviceRevenue: number;
    noServiceHours: number;
    percentageRate: number;
    individualRate: number;
    usesIndividualRate: boolean;
    usesServiceTherapistRate: boolean;
    hasPercentageBasedServices: boolean;
    hasMixedPercentageAndServiceRates: boolean;
    percentageAmount: number;
    groupServicesCount: number;
    groupServicesAmount: number;
    baseRate: number;
    baseAmount: number;
    combinedAmount: number;
    calculationDetails: { eventId: string; date: string; start: string; end: string; method: string; amount: number; startMs: number }[];
    rows: RevenueRow[];
    totalRevenue: number;
};

type ActiveReportType = 'therapist' | 'revenue' | null;

const ReportsPage: React.FC = () => {
    const now = new Date();
    const defaults = getDefaultDateRange(now);
    const [startDate, setStartDate] = useState(defaults.startDate);
    const [endDate, setEndDate] = useState(defaults.endDate);
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<TherapistReport[]>([]);
    const [revenueData, setRevenueData] = useState<TherapistRevenueReport[]>([]);
    const [activeReport, setActiveReport] = useState<ActiveReportType>(null);
    const [rangeLabel, setRangeLabel] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [infoMessage, setInfoMessage] = useState('');
    const [overlapWarnings, setOverlapWarnings] = useState<string[]>([]);
    const [expandedServiceRows, setExpandedServiceRows] = useState<Record<string, boolean>>({});
    const [expandedSummaryRows, setExpandedSummaryRows] = useState<Record<string, boolean>>({});
    const [assumptionsExpanded, setAssumptionsExpanded] = useState(false);

    async function handleGenerate() {
        setLoading(true);
        setError(null);
        setInfoMessage('');
        setActiveReport(null);
        setReportData([]);
        setRevenueData([]);
        setOverlapWarnings([]);
        setExpandedServiceRows({});
        setExpandedSummaryRows({});
        try {
            const prepared = await fetchTherapistReportData(startDate, endDate);
            setReportData(prepared);
            const label = buildRangeLabel(startDate, endDate);
            setRangeLabel(label);
            setActiveReport('therapist');
            setOverlapWarnings(buildOverlapWarnings(prepared));
            if (prepared.length === 0) {
                setInfoMessage('Brak wydarzeń w wybranym okresie.');
            }
        } catch (err: any) {
            console.error('[reports] unable to generate report', err);
            setError(err.message ?? 'Nie udało się wygenerować raportu.');
            setReportData([]);
            setRangeLabel('');
        } finally {
            setLoading(false);
        }
    }

    async function handleGenerateRevenue() {
        setLoading(true);
        setError(null);
        setInfoMessage('');
        setActiveReport(null);
        setReportData([]);
        setRevenueData([]);
        setOverlapWarnings([]);
        setExpandedServiceRows({});
        setExpandedSummaryRows({});
        try {
            const prepared = await fetchTherapistReportData(startDate, endDate);
            const generatedRevenue = buildRevenueReport(prepared);
            setRevenueData(generatedRevenue);
            const label = buildRangeLabel(startDate, endDate);
            setRangeLabel(label);
            setActiveReport('revenue');
            setOverlapWarnings(buildOverlapWarnings(prepared));
            if (generatedRevenue.length === 0) {
                setInfoMessage('Brak wydarzeń w wybranym okresie.');
            }
        } catch (err: any) {
            console.error('[reports] unable to generate revenue report', err);
            setError(err.message ?? 'Nie udało się wygenerować raportu przychodowego.');
            setRevenueData([]);
            setRangeLabel('');
        } finally {
            setLoading(false);
        }
    }

    function handleDownloadCsv() {
        if (activeReport === 'therapist' && reportData.length > 0) {
            const csv = buildCsv(reportData);
            downloadCsv(csv, `raport_terapeuci_${rangeLabel || `${startDate}_${endDate}`}.csv`);
            return;
        }
        if (activeReport === 'revenue' && revenueData.length > 0) {
            const csv = buildRevenueCsv(revenueData);
            downloadCsv(csv, `raport_przychodowy_${rangeLabel || `${startDate}_${endDate}`}.csv`);
            return;
        }
        if (activeReport === 'revenue') {
            setInfoMessage('Najpierw wygeneruj raport przychodowy.');
            return;
        }
        if (activeReport === 'therapist') {
            setInfoMessage('Najpierw wygeneruj raport terapeutyczny.');
            return;
        }
        setInfoMessage('Najpierw wygeneruj raport.');
    }

    function toggleServiceRowDetails(rowKey: string) {
        setExpandedServiceRows((prev) => ({
            ...prev,
            [rowKey]: !prev[rowKey]
        }));
    }

    function toggleSummaryRowDetails(rowKey: string) {
        setExpandedSummaryRows((prev) => ({
            ...prev,
            [rowKey]: !prev[rowKey]
        }));
    }

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Raporty terapeutyczne</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen>
                <IonCard className="report-card">
                    <IonCardHeader className="report-clickable-header" onClick={() => setAssumptionsExpanded((prev) => !prev)}>
                        <IonCardTitle>Założenia raportu {assumptionsExpanded ? '▲' : '▼'}</IonCardTitle>
                    </IonCardHeader>
                    {assumptionsExpanded && (
                        <IonCardContent>
                            <ul>
                                <li>Rozliczenia ilości zajęć i przychodu uwzględniają wyłącznie wpisy o statusie "zrealizowany".</li>
                                <li>Zakres raportu ustawiasz przez datę początkową i datę końcową (domyślnie 26 poprzedniego miesiąca - 25 bieżącego miesiąca).</li>
                                <li>Jeśli wizyty tego samego użytkownika nachodzą na siebie czasowo, do ilości zajęć i rozliczeń liczona jest tylko pierwsza w kolejności; pominięte terminy są pokazywane na czerwono.</li>
                                <li>Dla usług oznaczonych jako "Zajęcia grupowe" wpisy w tym samym czasie są liczone tylko raz.</li>
                                <li>W tabeli szczegółowej przychodu stawka za zajęcia pochodzi z ceny usługi z cennika.</li>
                                <li>W podsumowaniu, w kolumnie "Przychód * %" (dla terapeuty), gdy usługa ma ustawioną "Cenę dla terapeuty" większą od 0, używana jest ta cena pomnożona przez ilość zajęć z usługą.</li>
                                <li>Dla pozycji "brak usługi" rozliczenie terapeuty liczone jest ze "stawki podstawowej" użytkownika.</li>
                                <li>W tabeli szczegółowej "Suma przychodów" pomija pozycje "brak usługi".</li>
                            </ul>
                        </IonCardContent>
                    )}
                </IonCard>
                <div className="reports-controls ion-padding">
                    <div className="reports-controls-grid">
                        <IonItem>
                            <IonLabel position="stacked">Data początkowa</IonLabel>
                            <IonInput
                                type="date"
                                value={startDate}
                                onIonChange={(event) => setStartDate(String(event.detail.value ?? ''))}
                            ></IonInput>
                        </IonItem>
                        <IonItem>
                            <IonLabel position="stacked">Data końcowa</IonLabel>
                            <IonInput
                                type="date"
                                value={endDate}
                                onIonChange={(event) => setEndDate(String(event.detail.value ?? ''))}
                            ></IonInput>
                        </IonItem>
                    </div>
                    <div className="reports-actions">
                        <IonButton onClick={handleGenerate} disabled={loading} expand="block">
                            generuj raport
                        </IonButton>
                        <IonButton
                            color="secondary"
                            onClick={handleGenerateRevenue}
                            disabled={loading}
                            expand="block"
                        >
                            generuj raport przychodowy
                        </IonButton>
                        <IonButton
                            color="medium"
                            onClick={handleDownloadCsv}
                            disabled={loading || activeReport === null}
                            expand="block"
                        >
                            pobierz raport (CSV)
                        </IonButton>
                    </div>
                </div>

                {rangeLabel && reportData.length > 0 && (
                    <IonText className="reports-meta">
                        Raport za {rangeLabel}
                    </IonText>
                )}

                {loading && (
                    <div className="reports-spinner">
                        <IonSpinner />
                    </div>
                )}

                {error && (
                    <IonText color="danger" className="reports-meta">
                        {error}
                    </IonText>
                )}

                {!error && infoMessage && !loading && (
                    <IonText color="medium" className="reports-meta">
                        {infoMessage}
                    </IonText>
                )}

                {!loading && !error && overlapWarnings.length > 0 && (
                    <IonText color="warning" className="reports-meta">
                        Uwaga: wykryto nakładające się godziny przypisane użytkownikowi. {overlapWarnings.join(' | ')}
                    </IonText>
                )}

                {!loading && !error && reportData.map((therapist) => (
                    <IonCard key={therapist.therapistId} className="report-card">
                        <IonCardHeader>
                            <IonCardTitle>{therapist.therapistName}</IonCardTitle>
                            <IonText color="medium">
                                Łącznie {formatDuration(therapist.totalMinutes)} ({minutesToHours(therapist.totalMinutes)}h)
                            </IonText>
                        </IonCardHeader>
                        <IonCardContent>
                            <div className="report-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Data</th>
                                            <th>Godziny</th>
                                            <th>Czas</th>
                                            <th>Usługa</th>
                                            <th>Status</th>
                                            <th>Klient</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {therapist.sessions.map((session) => (
                                            <tr key={session.eventId}>
                                                <td>{session.date}</td>
                                                <td>{session.start} - {session.end}</td>
                                                <td>{session.durationLabel}</td>
                                                <td>{session.serviceName}</td>
                                                <td>{session.statusLabel}</td>
                                                <td>
                                                    <div>{session.clientName}</div>
                                                    {(session.clientPhone || session.clientEmail) && (
                                                        <div className="report-contact">
                                                            {[session.clientPhone, session.clientEmail].filter(Boolean).join(' | ')}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="report-summary">
                                {STATUS_DEFINITIONS.map((status) => {
                                    const minutes = therapist.statusSummary[status.id] ?? 0;
                                    if (minutes === 0) {
                                        return null;
                                    }
                                    return (
                                        <span key={status.id} className="report-summary-chip">
                                            {status.label}: {minutesToHours(minutes)}h ({minutes}m)
                                        </span>
                                    );
                                })}
                            </div>
                            <div className="report-summary">
                                {Object.entries(therapist.serviceSummary)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([serviceName, minutes]) => (
                                        <span key={serviceName} className="report-summary-chip">
                                            {serviceName}: {minutesToHours(minutes)}h ({minutes}m)
                                        </span>
                                    ))}
                            </div>
                        </IonCardContent>
                    </IonCard>
                ))}

                {!loading && !error && revenueData.length > 0 && (
                    <>
                        <IonText className="reports-meta">
                            Raport przychodowy terapeutów
                        </IonText>
                        <IonCard className="report-card">
                            <IonCardHeader>
                                <IonCardTitle>Podsumowanie ({rangeLabel})</IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <div className="report-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Imię i nazwisko</th>
                                                <th>Ilość zajęć z usługą</th>
                                                <th>Przychód</th>
                                                <th>%</th>
                                                <th>Dla terapeuty</th>
                                                <th>Ilość usług grupowych</th>
                                                <th>Usługi grupowe</th>
                                                <th>Ilość zajęć bez usługi</th>
                                                <th>Stawka podstawowa</th>
                                                <th>Ilość zajęć * stawka podstawowa</th>
                                                <th>Do wypłaty</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {revenueData.map((therapist) => {
                                                const summaryRowKey = `summary-${therapist.therapistId}`;
                                                const isExpanded = Boolean(expandedSummaryRows[summaryRowKey]);
                                                return (
                                                    <Fragment key={summaryRowKey}>
                                                        <tr className="report-clickable-row" onClick={() => toggleSummaryRowDetails(summaryRowKey)}>
                                                            <td>{therapist.therapistName}</td>
                                                            <td>{therapist.serviceHours}</td>
                                                            <td>{formatMoney(therapist.serviceRevenue)}</td>
                                                            <td>
                                                                {therapist.hasPercentageBasedServices
                                                                    ? (
                                                                        <span className={therapist.hasMixedPercentageAndServiceRates ? 'report-percent-mixed' : ''}>
                                                                            {therapist.percentageRate}
                                                                        </span>
                                                                    )
                                                                    : '-'}
                                                            </td>
                                                            <td>{formatMoney(therapist.percentageAmount)}</td>
                                                            <td>{therapist.groupServicesCount}</td>
                                                            <td>{formatMoney(therapist.groupServicesAmount)}</td>
                                                            <td>{therapist.noServiceHours}</td>
                                                            <td>{formatMoney(therapist.baseRate)}</td>
                                                            <td>{formatMoney(therapist.baseAmount)}</td>
                                                            <td>{formatMoney(therapist.combinedAmount)}</td>
                                                        </tr>
                                                        {isExpanded && (
                                                            <tr className="report-details-row">
                                                                <td colSpan={11}>
                                                                    {therapist.calculationDetails.length === 0
                                                                        ? 'Brak szczegółów wyliczenia.'
                                                                        : (
                                                                            <div>
                                                                                {therapist.calculationDetails
                                                                                    .sort((a, b) => a.startMs - b.startMs)
                                                                                    .map((item) => (
                                                                                        <div key={`calc-${therapist.therapistId}-${item.eventId}-${item.startMs}`}>
                                                                                            {item.date} {item.start}-{item.end}: {item.method} = {formatMoney(item.amount)}
                                                                                        </div>
                                                                                    ))}
                                                                            </div>
                                                                        )}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </IonCardContent>
                        </IonCard>
                    </>
                )}

                {!loading && !error && revenueData.map((therapist) => (
                    <IonCard key={`revenue-${therapist.therapistId}`} className="report-card">
                        <IonCardHeader>
                            <IonCardTitle>{therapist.therapistName} - przychód</IonCardTitle>
                            <IonText color="medium">
                                Suma przychodów: {formatMoney(therapist.totalRevenue)}
                            </IonText>
                        </IonCardHeader>
                        <IonCardContent>
                            <div className="report-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Usługa</th>
                                            <th>Stawka za zajęcia</th>
                                            <th>Ilość zajęć</th>
                                            <th>Iloczyn</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {therapist.rows.map((row) => {
                                            const rowKey = `${therapist.therapistId}__${row.serviceName}__${row.hourlyRate}`;
                                            const isExpanded = Boolean(expandedServiceRows[rowKey]);
                                            return (
                                                <Fragment key={rowKey}>
                                                    <tr className="report-clickable-row" onClick={() => toggleServiceRowDetails(rowKey)}>
                                                        <td>{row.serviceName}</td>
                                                        <td>{formatMoney(row.hourlyRate)}</td>
                                                        <td>{row.totalHours}</td>
                                                        <td>{formatMoney(row.revenue)}</td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr className="report-details-row">
                                                            <td colSpan={4}>
                                                                <div>
                                                                    {row.occurrences.length === 0
                                                                        ? 'Brak wpisów realizacji.'
                                                                        : row.occurrences
                                                                            .sort((a, b) => a.startMs - b.startMs)
                                                                            .map((occurrence) => `${occurrence.date} ${occurrence.start}-${occurrence.end}`)
                                                                            .join(', ')}
                                                                </div>
                                                                {row.omittedOccurrences.length > 0 && (
                                                                    <div className="report-omitted-occurrences">
                                                                        Pominięte: {row.omittedOccurrences
                                                                            .sort((a, b) => a.startMs - b.startMs)
                                                                            .map((occurrence) => `${occurrence.date} ${occurrence.start}-${occurrence.end}`)
                                                                            .join(', ')}
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </IonCardContent>
                    </IonCard>
                ))}
            </IonContent>
        </IonPage>
    );
};

function getDefaultDateRange(now: Date) {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 26, 0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth(), 25, 0, 0, 0, 0);
    return {
        startDate: formatDate(start),
        endDate: formatDate(end)
    };
}

function parseDateInput(value: string) {
    const parts = value.split('-').map((part) => Number(part));
    if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
        return null;
    }
    const [year, month, day] = parts;
    return new Date(year, month - 1, day, 0, 0, 0, 0);
}

function getReportRange(startDate: string, endDate: string) {
    const start = parseDateInput(startDate);
    const endInclusive = parseDateInput(endDate);
    if (!start || !endInclusive) {
        throw new Error('Uzupełnij poprawnie datę początkową i końcową.');
    }
    if (endInclusive.getTime() < start.getTime()) {
        throw new Error('Data końcowa nie może być wcześniejsza niż data początkowa.');
    }
    const endExclusive = new Date(endInclusive.getFullYear(), endInclusive.getMonth(), endInclusive.getDate() + 1, 0, 0, 0, 0);
    return { start, end: endExclusive, endInclusive };
}

function buildRangeLabel(startDate: string, endDate: string) {
    const { start, endInclusive } = getReportRange(startDate, endDate);
    return `${formatDate(start)} - ${formatDate(endInclusive)}`;
}

function buildTherapistName(user: any, fallbackId: string) {
    if (!user) {
        return fallbackId === 'unassigned' ? '(nieprzypisane)' : '(nieznany terapeuta)';
    }
    const full = `${user.name ?? ''} ${user.surname ?? ''}`.trim();
    if (full.length > 0) {
        return full;
    }
    return user.email ?? fallbackId;
}

function formatClientName(client?: any) {
    if (!client) {
        return '(brak klienta)';
    }
    const full = `${client.name ?? ''} ${client.surname ?? ''}`.trim();
    if (full.length > 0) {
        return full;
    }
    return client.email ?? client.phone ?? '(brak klienta)';
}

function formatDate(value: Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function formatTime(value: Date) {
    const hours = String(value.getHours()).padStart(2, '0');
    const minutes = String(value.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
}

function formatDuration(minutes: number) {
    if (minutes <= 0) {
        return '0m';
    }
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (hours > 0 && rest > 0) {
        return `${hours}h ${rest}m`;
    }
    if (hours > 0) {
        return `${hours}h`;
    }
    return `${rest}m`;
}

function minutesToHours(minutes: number) {
    return Math.round((minutes / 60) * 100) / 100;
}

function formatMoney(value: number) {
    return `${Math.round(value * 100) / 100} zł`;
}

function createEmptyStatusSummary() {
    const summary: Record<number, number> = {};
    STATUS_DEFINITIONS.forEach((status) => {
        summary[status.id] = 0;
    });
    return summary;
}

async function fetchTherapistReportData(startDate: string, endDate: string): Promise<TherapistReport[]> {
    const companyId = getCompanyId();
    if (!companyId || companyId === '???') {
        throw new Error('Brak wybranego workspace. Przełącz się na firmę i spróbuj ponownie.');
    }
    const { start, end } = getReportRange(startDate, endDate);
    const eventsRef = collection(db, `companies/${companyId}/events`);
    const eventsQuery = query(eventsRef, where('start', '>=', start), where('start', '<', end), orderBy('start'));
    const snapshot = await getDocs(eventsQuery);

    const users = getUsers();
    const clients = getClients();
    const userMap = new Map(users.map((user: any) => [user.id, user]));
    const clientMap = new Map(clients.map((client: any) => [client.id, client]));
    const servicesRef = collection(db, `companies/${companyId}/services`);
    const servicesSnapshot = await getDocs(servicesRef);
    const serviceMap = new Map(servicesSnapshot.docs.map((serviceDoc) => [serviceDoc.id, serviceDoc.data()]));

    const grouped = new Map<string, TherapistReport>();
    snapshot.forEach((doc) => {
        const data: any = doc.data();
        if (!data.start) {
            return;
        }
        const startDate = typeof data.start.toDate === 'function' ? data.start.toDate() : new Date(data.start);
        const endDate = data.end ? (typeof data.end.toDate === 'function' ? data.end.toDate() : new Date(data.end)) : new Date(startDate);
        const durationMinutes = Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000));
        const therapistId = data.userId ?? 'unassigned';
        const therapistUser = userMap.get(therapistId);
        if (!grouped.has(therapistId)) {
            grouped.set(therapistId, {
                therapistId,
                therapistName: buildTherapistName(therapistUser, therapistId),
                baseRate: Number.isFinite(Number(therapistUser?.baseRate)) ? Number(therapistUser?.baseRate) : 0,
                percentageRate: Number.isFinite(Number(therapistUser?.percentageRate)) ? Number(therapistUser?.percentageRate) : 0,
                individualRate: Number.isFinite(Number(therapistUser?.individualRate)) ? Number(therapistUser?.individualRate) : 0,
                overlapCount: 0,
                overlapDetails: [],
                totalMinutes: 0,
                sessions: [],
                statusSummary: createEmptyStatusSummary(),
                serviceSummary: {}
            });
        }
        const report = grouped.get(therapistId)!;
        const client = data.clientId ? clientMap.get(data.clientId) : undefined;
        const service = data.serviceId ? serviceMap.get(data.serviceId) : undefined;
        const hasService = Boolean(data.serviceId && service);
        const isGroupClasses = Boolean(service?.groupClasses);
        const serviceName = service?.name ?? '(brak usługi)';
        const serviceCatalogRate = hasService ? (Number(service?.price) || 0) : 0;
        const serviceTherapistRate = hasService ? (Number(service?.therapistPrice) || 0) : 0;
        const servicePrice = isGroupClasses
            ? (Number(service?.therapistPrice) || 0)
            : (Number(service?.price) || 0);
        const serviceTime = Number(service?.time) || 0;
        const serviceHourlyRate = !hasService
            ? (report.baseRate ?? 0)
            : serviceTime > 0
                ? Math.round(((servicePrice * 60) / serviceTime) * 100) / 100
                : 0;
        const statusId = typeof data.status === 'number' ? data.status : 1;
        report.sessions.push({
            eventId: doc.id,
            date: formatDate(startDate),
            start: formatTime(startDate),
            end: formatTime(endDate),
            startMs: startDate.getTime(),
            endMs: endDate.getTime(),
            durationMinutes,
            price: Number(data.price) || 0,
            serviceCatalogRate,
            serviceTherapistRate,
            serviceHourlyRate,
            durationLabel: formatDuration(durationMinutes),
            statusId,
            statusLabel: STATUS_LOOKUP[statusId] ?? `status_${statusId}`,
            serviceName,
            clientName: formatClientName(client),
            clientPhone: client?.phone ?? '',
            clientEmail: client?.email ?? '',
            comments: data.comments ?? '',
            managerComments: data.managerComments ?? '',
            hasService,
            isGroupClasses
        });
    });

    return Array.from(grouped.values())
        .map((item) => {
            const sessions = item.sessions.sort((a, b) => (a.date === b.date ? a.start.localeCompare(b.start) : a.date.localeCompare(b.date)));
            const realizedSessions = sessions.filter((session) => session.statusId === REALIZED_STATUS_ID);
            const sessionsForOverlapWarnings = realizedSessions.filter((session) => !session.isGroupClasses);
            const overlapDetails = getOverlappingIntervals(sessionsForOverlapWarnings.map((session) => ({
                startMs: session.startMs,
                endMs: session.endMs
            })));
            const totalMinutes = getMergedMinutes(realizedSessions.map((session) => ({ startMs: session.startMs, endMs: session.endMs })));

            const statusSummary = createEmptyStatusSummary();
            STATUS_DEFINITIONS.forEach((status) => {
                const minutes = getMergedMinutes(
                    realizedSessions
                        .filter((session) => session.statusId === status.id)
                        .map((session) => ({ startMs: session.startMs, endMs: session.endMs }))
                );
                statusSummary[status.id] = minutes;
            });

            const serviceSummary: Record<string, number> = {};
            const serviceNames = Array.from(new Set(realizedSessions.map((session) => session.serviceName)));
            serviceNames.forEach((serviceName) => {
                const minutes = getMergedMinutes(
                    realizedSessions
                        .filter((session) => session.serviceName === serviceName)
                        .map((session) => ({ startMs: session.startMs, endMs: session.endMs }))
                );
                serviceSummary[serviceName] = minutes;
            });

            return {
                ...item,
                sessions,
                overlapCount: overlapDetails.length,
                overlapDetails,
                totalMinutes,
                statusSummary,
                serviceSummary
            };
        })
        .sort((a, b) => a.therapistName.localeCompare(b.therapistName));
}

function buildRevenueReport(data: TherapistReport[]): TherapistRevenueReport[] {
    return data.map((therapist) => {
        const usesIndividualRate = (therapist.individualRate ?? 0) > 0;
        const realizedSessions = therapist.sessions
            .filter((session) => session.statusId === REALIZED_STATUS_ID)
            .sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);
        const deduplicatedGroupSessions = dedupeGroupSessionsByExactTime(realizedSessions);
        const billableSessions = getFirstNonOverlappingSessions(deduplicatedGroupSessions);
        const deduplicatedIds = new Set(deduplicatedGroupSessions.map((session) => session.eventId));
        const billableIds = new Set(billableSessions.map((session) => session.eventId));
        const omittedSessions = [
            ...realizedSessions.filter((session) => !deduplicatedIds.has(session.eventId)),
            ...deduplicatedGroupSessions.filter((session) => !billableIds.has(session.eventId))
        ];

        const grouped = new Map<string, RevenueRow>();

        const getSessionRate = (session: SessionRow) => {
            if (session.hasService && (session.serviceTherapistRate ?? 0) > 0) {
                return session.serviceTherapistRate;
            }
            if (session.hasService && usesIndividualRate) {
                return therapist.individualRate ?? 0;
            }
            return session.serviceHourlyRate;
        };

        const getRevenueRate = (session: SessionRow) => {
            if (session.hasService) {
                return session.serviceCatalogRate ?? 0;
            }
            return session.serviceHourlyRate;
        };

        billableSessions.forEach((session) => {
            const hourlyRate = getRevenueRate(session);
            const groupKey = `${session.serviceName}__${hourlyRate}`;
            if (!grouped.has(groupKey)) {
                grouped.set(groupKey, {
                    serviceName: session.serviceName,
                    hourlyRate,
                    totalMinutes: 0,
                    totalHours: 0,
                    revenue: 0,
                    occurrences: [],
                    omittedOccurrences: []
                });
            }
            const row = grouped.get(groupKey)!;
            row.totalHours += 1;
            row.occurrences.push({
                eventId: session.eventId,
                date: session.date,
                start: session.start,
                end: session.end,
                startMs: session.startMs
            });
        });

        const rows = Array.from(grouped.values())
            .map((row) => {
                const revenue = Math.round((row.totalHours * row.hourlyRate) * 100) / 100;
                const groupKey = `${row.serviceName}__${row.hourlyRate}`;
                const omittedOccurrences = omittedSessions
                    .filter((session) => `${session.serviceName}__${getRevenueRate(session)}` === groupKey)
                    .map((session) => ({
                        eventId: session.eventId,
                        date: session.date,
                        start: session.start,
                        end: session.end,
                        startMs: session.startMs
                    }));
                return {
                    ...row,
                    totalMinutes: row.totalHours,
                    revenue,
                    omittedOccurrences
                };
            })
            .sort((a, b) => b.revenue - a.revenue);

        const serviceHours = billableSessions.filter((session) => session.hasService && !session.isGroupClasses).length;
        const noServiceHours = billableSessions.filter((session) => !session.hasService).length;
        const usesServiceTherapistRate = billableSessions.some((session) => session.hasService && (session.serviceTherapistRate ?? 0) > 0);
        const serviceRevenue = Math.round(
            billableSessions
                .filter((session) => session.hasService)
                .reduce((sum, session) => sum + (session.serviceCatalogRate || 0), 0) * 100
        ) / 100;
        const groupServicesCount = billableSessions.filter((session) => session.hasService && session.isGroupClasses && (session.serviceTherapistRate ?? 0) > 0).length;
        const groupServicesAmount = Math.round(
            billableSessions
                .filter((session) => session.hasService && session.isGroupClasses && (session.serviceTherapistRate ?? 0) > 0)
                .reduce((sum, session) => sum + (session.serviceTherapistRate ?? 0), 0) * 100
        ) / 100;
        const percentageRate = Math.round((therapist.percentageRate ?? 0) * 100) / 100;
        const individualRate = Math.round((therapist.individualRate ?? 0) * 100) / 100;
        const baseRate = Math.round((therapist.baseRate ?? 0) * 100) / 100;
        const usePercentageFromRevenue = !usesIndividualRate && percentageRate > 0;
        const nonGroupServiceSessions = billableSessions.filter(
            (session) => session.hasService && !(session.isGroupClasses && (session.serviceTherapistRate ?? 0) > 0)
        );
        const nonGroupTherapistPricedAmount = Math.round(
            nonGroupServiceSessions
                .filter((session) => (session.serviceTherapistRate ?? 0) > 0)
                .reduce((sum, session) => sum + (session.serviceTherapistRate ?? 0), 0) * 100
        ) / 100;
        const percentageBaseRevenue = Math.round(
            nonGroupServiceSessions
                .filter((session) => (session.serviceTherapistRate ?? 0) <= 0)
                .reduce((sum, session) => sum + (session.serviceCatalogRate || 0), 0) * 100
        ) / 100;
        const hasPercentageBasedServices = usePercentageFromRevenue && percentageBaseRevenue > 0;
        const hasServicePricedServices =
            nonGroupServiceSessions.some((session) => (session.serviceTherapistRate ?? 0) > 0)
            || groupServicesCount > 0;
        const hasMixedPercentageAndServiceRates = hasPercentageBasedServices && hasServicePricedServices;

        const percentageAmount = usePercentageFromRevenue
            ? Math.round((nonGroupTherapistPricedAmount + ((percentageBaseRevenue * percentageRate) / 100)) * 100) / 100
            : Math.round(nonGroupServiceSessions.reduce((sum, session) => sum + getSessionRate(session), 0) * 100) / 100;
        const baseAmount = Math.round((noServiceHours * baseRate) * 100) / 100;
        const combinedAmount = Math.round((percentageAmount + groupServicesAmount + baseAmount) * 100) / 100;

        const calculationDetails = billableSessions.map((session) => {
            if (!session.hasService) {
                return {
                    eventId: session.eventId,
                    date: session.date,
                    start: session.start,
                    end: session.end,
                    method: `brak usługi (stawka podstawowa ${baseRate})`,
                    amount: baseRate,
                    startMs: session.startMs
                };
            }

            if (session.isGroupClasses && (session.serviceTherapistRate ?? 0) > 0) {
                return {
                    eventId: session.eventId,
                    date: session.date,
                    start: session.start,
                    end: session.end,
                    method: `usługa grupowa (cena dla terapeuty ${session.serviceTherapistRate})`,
                    amount: session.serviceTherapistRate,
                    startMs: session.startMs
                };
            }

            if ((session.serviceTherapistRate ?? 0) > 0) {
                return {
                    eventId: session.eventId,
                    date: session.date,
                    start: session.start,
                    end: session.end,
                    method: `cena dla terapeuty z usługi (${session.serviceTherapistRate})`,
                    amount: session.serviceTherapistRate,
                    startMs: session.startMs
                };
            }

            if (usePercentageFromRevenue) {
                const amount = Math.round(((session.serviceCatalogRate || 0) * percentageRate) * 100) / 10000;
                return {
                    eventId: session.eventId,
                    date: session.date,
                    start: session.start,
                    end: session.end,
                    method: `procentowo (${session.serviceCatalogRate} * ${percentageRate}%)`,
                    amount,
                    startMs: session.startMs
                };
            }

            if (usesIndividualRate) {
                return {
                    eventId: session.eventId,
                    date: session.date,
                    start: session.start,
                    end: session.end,
                    method: `stawka indywidualna (${individualRate})`,
                    amount: individualRate,
                    startMs: session.startMs
                };
            }

            return {
                eventId: session.eventId,
                date: session.date,
                start: session.start,
                end: session.end,
                method: `stawka z usługi (${getSessionRate(session)})`,
                amount: getSessionRate(session),
                startMs: session.startMs
            };
        });
        const totalRevenue = Math.round(
            rows
                .filter((row) => row.serviceName !== '(brak usługi)')
                .reduce((sum, row) => sum + row.revenue, 0) * 100
        ) / 100;
        return {
            therapistId: therapist.therapistId,
            therapistName: therapist.therapistName,
            overlapCount: therapist.overlapCount,
            serviceHours,
            serviceRevenue,
            noServiceHours,
            percentageRate,
            individualRate,
            usesIndividualRate,
            usesServiceTherapistRate,
            hasPercentageBasedServices,
            hasMixedPercentageAndServiceRates,
            percentageAmount,
            groupServicesCount,
            groupServicesAmount,
            baseRate,
            baseAmount,
            combinedAmount,
            calculationDetails,
            rows,
            totalRevenue
        };
    });
}

function dedupeGroupSessionsByExactTime(sessions: SessionRow[]) {
    const seenGroupSlots = new Set<string>();
    return sessions.filter((session) => {
        if (!session.isGroupClasses) {
            return true;
        }
        const slotKey = `${session.serviceName}__${session.startMs}__${session.endMs}`;
        if (seenGroupSlots.has(slotKey)) {
            return false;
        }
        seenGroupSlots.add(slotKey);
        return true;
    });
}

function getFirstNonOverlappingSessions(sessions: SessionRow[]) {
    if (sessions.length <= 1) {
        return sessions;
    }

    const selected: SessionRow[] = [];
    let lastEnd = -Infinity;
    sessions.forEach((session) => {
        if (session.startMs >= lastEnd) {
            selected.push(session);
            lastEnd = session.endMs;
        }
    });
    return selected;
}

function getOverlappingIntervals(intervals: { startMs: number; endMs: number }[]) {
    const sorted = [...intervals]
        .filter((interval) => Number.isFinite(interval.startMs) && Number.isFinite(interval.endMs) && interval.endMs > interval.startMs)
        .sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);

    if (sorted.length <= 1) {
        return [];
    }

    const overlaps: { startMs: number; endMs: number }[] = [];
    let maxEnd = sorted[0].endMs;
    for (let index = 1; index < sorted.length; index += 1) {
        const current = sorted[index];
        if (current.startMs < maxEnd) {
            overlaps.push({
                startMs: current.startMs,
                endMs: Math.min(current.endMs, maxEnd)
            });
        }
        maxEnd = Math.max(maxEnd, current.endMs);
    }
    return overlaps;
}

function buildOverlapWarnings(data: TherapistReport[]) {
    return data
        .filter((therapist) => therapist.overlapCount > 0)
        .map((therapist) => {
            const details = therapist.overlapDetails
                .map((overlap) => formatOverlapRange(overlap.startMs, overlap.endMs))
                .join(', ');
            return `${therapist.therapistName}: ${therapist.overlapCount} (${details})`;
        });
}

function formatOverlapRange(startMs: number, endMs: number) {
    const start = new Date(startMs);
    const end = new Date(endMs);
    return `${formatDate(start)} ${formatTime(start)}-${formatTime(end)}`;
}

function getMergedMinutes(intervals: { startMs: number; endMs: number }[]) {
    if (intervals.length === 0) {
        return 0;
    }
    const sorted = [...intervals]
        .filter((interval) => Number.isFinite(interval.startMs) && Number.isFinite(interval.endMs) && interval.endMs > interval.startMs)
        .sort((a, b) => a.startMs - b.startMs);
    if (sorted.length === 0) {
        return 0;
    }

    let mergedStart = sorted[0].startMs;
    let mergedEnd = sorted[0].endMs;
    let totalMs = 0;

    for (let index = 1; index < sorted.length; index += 1) {
        const current = sorted[index];
        if (current.startMs <= mergedEnd) {
            mergedEnd = Math.max(mergedEnd, current.endMs);
            continue;
        }
        totalMs += mergedEnd - mergedStart;
        mergedStart = current.startMs;
        mergedEnd = current.endMs;
    }

    totalMs += mergedEnd - mergedStart;
    return Math.max(0, Math.round(totalMs / 60000));
}

function buildCsv(data: TherapistReport[]) {
    const header = [
        'terapeuta',
        'data',
        'godzina_od',
        'godzina_do',
        'czas_minuty',
        'czas_opis',
        'usluga',
        'status',
        'klient',
        'telefon',
        'email',
        'komentarz',
        'uwagi_managera'
    ];
    const rows = [header.map(csvEscape).join(';')];
    data.forEach((therapist) => {
        therapist.sessions.forEach((session) => {
            rows.push([
                csvEscape(therapist.therapistName),
                csvEscape(session.date),
                csvEscape(session.start),
                csvEscape(session.end),
                csvEscape(session.durationMinutes),
                csvEscape(session.durationLabel),
                csvEscape(session.serviceName),
                csvEscape(session.statusLabel),
                csvEscape(session.clientName),
                csvEscape(session.clientPhone),
                csvEscape(session.clientEmail),
                csvEscape(session.comments ?? ''),
                csvEscape(session.managerComments ?? '')
            ].join(';'));
        });
        rows.push([
            csvEscape(`${therapist.therapistName} - suma`),
            '',
            '',
            '',
            csvEscape(therapist.totalMinutes),
            csvEscape(formatDuration(therapist.totalMinutes)),
            '',
            '',
            '',
            '',
            '',
            '',
            ''
        ].join(';'));
        STATUS_DEFINITIONS.forEach((status) => {
            const minutes = therapist.statusSummary[status.id] ?? 0;
            if (minutes === 0) {
                return;
            }
            rows.push([
                csvEscape(`${therapist.therapistName} - ${status.label}`),
                '',
                '',
                '',
                csvEscape(minutes),
                csvEscape(formatDuration(minutes)),
                '',
                csvEscape(status.label),
                '',
                '',
                '',
                '',
                ''
            ].join(';'));
        });
    });
    return rows.join('\n');
}

function buildRevenueCsv(data: TherapistRevenueReport[]) {
    const header = ['terapeuta', 'usluga', 'stawka_za_zajecia', 'ilosc_zajec', 'iloczyn', 'suma_terapeuty'];
    const rows = [header.map(csvEscape).join(';')];
    data.forEach((therapist) => {
        therapist.rows.forEach((row) => {
            rows.push([
                csvEscape(therapist.therapistName),
                csvEscape(row.serviceName),
                csvEscape(row.hourlyRate),
                csvEscape(row.totalHours),
                csvEscape(row.revenue),
                ''
            ].join(';'));
        });
        rows.push([
            csvEscape(therapist.therapistName),
            '',
            '',
            '',
            '',
            csvEscape(therapist.totalRevenue)
        ].join(';'));
    });
    return rows.join('\n');
}

function downloadCsv(csv: string, fileName: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function csvEscape(value: string | number) {
    const safe = String(value ?? '');
    return '"' + safe.replace(/"/g, '""') + '"';
}

export default ReportsPage;
